import React, { useState, useEffect, useCallback } from "react";
import { X, ArrowRight, ArrowLeft, Check, Sparkle } from "phosphor-react";

// localStorage key prefix for tour state (user-specific)
const TOUR_COMPLETED_KEY_PREFIX = 'geni_tour_completed_';

// Tour step definitions
const TOUR_STEPS = [
  {
    id: 1,
    target: '[data-testid="upload-section"]',
    title: 'Upload Chart',
    content: 'Upload your chart here to automatically extract your trade.',
    placement: 'right'
  },
  {
    id: 2,
    target: '[data-testid="trade-result-section"]',
    title: 'Trade Result',
    content: 'Review and complete your trade details before saving.',
    placement: 'left'
  },
  {
    id: 3,
    target: '[data-testid="nav-open-positions"]',
    title: 'Open Positions',
    content: 'Here you can track trades that are still open.',
    placement: 'bottom'
  },
  {
    id: 4,
    target: '[data-testid="nav-history"]',
    title: 'History',
    content: 'Review your past trades and daily performance.',
    placement: 'bottom'
  },
  {
    id: 5,
    target: '[data-testid="nav-analytics"]',
    title: 'Analytics',
    content: 'Analyze your trading performance and identify patterns.',
    placement: 'bottom'
  },
  {
    id: 6,
    target: '[data-testid="nav-advanced"]',
    title: 'Advanced',
    content: 'Advanced insights help you understand deeper behavioral patterns.',
    placement: 'bottom'
  },
  {
    id: 7,
    target: '[data-testid="nav-performance"]',
    title: 'Performance',
    content: 'Track your capital growth and overall results.',
    placement: 'bottom'
  },
  {
    id: 8,
    target: '[data-testid="nav-alerts"]',
    title: 'Alerts',
    content: 'Set alerts to stay disciplined and control your risk.',
    placement: 'bottom'
  }
];

/**
 * Get the user-specific tour completion key
 */
const getTourKey = (userId) => {
  if (!userId) return null;
  return `${TOUR_COMPLETED_KEY_PREFIX}${userId}`;
};

/**
 * Check if tour has been completed for a specific user
 */
export const isTourCompleted = (userId) => {
  try {
    const key = getTourKey(userId);
    if (!key) return false;
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

/**
 * Mark tour as completed for a specific user
 */
export const markTourCompleted = (userId) => {
  try {
    const key = getTourKey(userId);
    if (!key) {
      console.warn('[Tour Debug] Cannot mark completed - no userId provided');
      return;
    }
    console.log('[Tour Debug] Marking tour completed for key:', key);
    localStorage.setItem(key, 'true');
  } catch (e) {
    console.error('[Tour Debug] Error saving completion:', e);
  }
};

/**
 * GuidedTour component
 * Props:
 *   - isOpen: boolean - whether tour is active
 *   - onClose: () => void - called when tour ends
 *   - userId: string - the current user's ID (for persistence)
 */
export default function GuidedTour({ isOpen, onClose, userId }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [targetRect, setTargetRect] = useState(null);

  // Calculate tooltip position based on target element
  const updateTooltipPosition = useCallback(() => {
    const step = TOUR_STEPS[currentStep];
    if (!step) return;

    const target = document.querySelector(step.target);
    if (!target) {
      // Target not found, try to show centered
      setTooltipPosition({
        top: window.innerHeight / 2 - 100,
        left: window.innerWidth / 2 - 175
      });
      setTargetRect(null);
      return;
    }

    const rect = target.getBoundingClientRect();
    setTargetRect(rect);

    const tooltipWidth = 350;
    const tooltipHeight = 150;
    const padding = 16;

    let top, left;

    switch (step.placement) {
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + padding;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - padding;
        break;
      case 'top':
        top = rect.top - tooltipHeight - padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'bottom':
      default:
        top = rect.bottom + padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
    }

    // Keep tooltip within viewport
    top = Math.max(padding, Math.min(window.innerHeight - tooltipHeight - padding, top));
    left = Math.max(padding, Math.min(window.innerWidth - tooltipWidth - padding, left));

    setTooltipPosition({ top, left });
  }, [currentStep]);

  // Update position on step change and window resize
  useEffect(() => {
    if (!isOpen) return;
    
    updateTooltipPosition();
    
    const handleResize = () => updateTooltipPosition();
    window.addEventListener('resize', handleResize);
    
    // Small delay to ensure DOM is ready
    const timer = setTimeout(updateTooltipPosition, 100);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [isOpen, currentStep, updateTooltipPosition]);

  // Reset step when tour opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    console.log('[Tour Debug] User skipped tour, userId:', userId);
    markTourCompleted(userId);
    onClose();
  };

  const handleFinish = () => {
    console.log('[Tour Debug] User finished tour, userId:', userId);
    markTourCompleted(userId);
    onClose();
  };

  if (!isOpen) return null;

  const step = TOUR_STEPS[currentStep];
  const isLastStep = currentStep === TOUR_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
        onClick={handleSkip}
        data-testid="tour-overlay"
      />

      {/* Highlight target element */}
      {targetRect && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
            borderRadius: '8px',
            border: '2px solid #00E676'
          }}
        />
      )}

      {/* Tooltip */}
      <div
        className="fixed z-[10000] w-[350px] bg-[#1A1A1A] border border-white/20 rounded-lg shadow-2xl"
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left
        }}
        data-testid="tour-tooltip"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Sparkle size={18} weight="fill" className="text-[#00E676]" />
            <span className="text-white font-semibold">{step.title}</span>
          </div>
          <button
            onClick={handleSkip}
            className="p-1 text-white/40 hover:text-white transition-colors"
            data-testid="tour-skip-button"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-white/80 text-sm leading-relaxed">
            {step.content}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-white/10">
          {/* Progress indicator */}
          <div className="flex items-center gap-1">
            {TOUR_STEPS.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx === currentStep ? 'bg-[#00E676]' : 'bg-white/20'
                }`}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <button
                onClick={handleBack}
                className="px-3 py-1.5 text-sm text-white/60 hover:text-white transition-colors flex items-center gap-1"
                data-testid="tour-back-button"
              >
                <ArrowLeft size={14} />
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-4 py-1.5 text-sm bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors flex items-center gap-1"
              data-testid="tour-next-button"
            >
              {isLastStep ? (
                <>
                  Finish <Check size={14} weight="bold" />
                </>
              ) : (
                <>
                  Next <ArrowRight size={14} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
